import React, { useContext, type FC } from 'react';

import { getColor, isDark } from '@lucid/theme';
import kebabCase from 'lodash/kebabCase';
import { useProductUrls, useSmartlingStrings } from '@lucid/hooks';

import type { Lucid } from '../../../../types/types';
import Box from '../../../../components/box/box';
import Container from '../../../../components/container/container';
import Heading from '../../../../components/heading/heading';
import LayoutContext from '../../../../components/context/layout-context';
import Link, { type LinkProps } from '../../../../components/link/link';
import TemplateCard from '../../../../components/cards/template-card/template-card';
import Text from '../../../../components/text/text';

export interface TemplateGridProps {
  /** The section background color */
  backgroundColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** The template cards */
  cards?: Lucid.Directus.Generated.Template_Card[];
  /** Allows the component to get additional styles. */
  className?: string;
  /** The section heading */
  heading?: string;
  /** Theme variant for the section heading */
  headingVariant?: Lucid.Theme.Component.Typography.Heading.VariantKeys;
  /** The section CTA */
  link?: Lucid.Directus.Generated.Links & LinkProps;
  /** Suporting text for the section */
  text?: string;
  /** Allows to pass in a Beacon event to the CTA link */
  beaconEvent?: Lucid.Beacon.Event;
}

const TemplateGrid: FC<TemplateGridProps> = ({
  backgroundColor = 'white',
  cards,
  className,
  heading,
  headingVariant = 'heading2XL',
  link,
  text,
  beaconEvent,
  ...rest
}) => {
  const { shortCode, urlLangPrefix, product } = useContext(LayoutContext);
  const { registerUrl } = useProductUrls(urlLangPrefix, product);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_consideration_template'],
    shortCode,
  });
  const backgroundColorValue = (typeof backgroundColor === 'object' && backgroundColor[product]) || backgroundColor;
  const hasDarkBackground = isDark(backgroundColorValue);

  return cards?.length ? (
    <Box
      {...rest}
      css={theme => ({
        padding: `${theme.space.s104}px 0`,
        backgroundColor: getColor({ color: backgroundColor, theme }),
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Container extraPadding={3}>
        {heading && (
          <Heading
            as="h2"
            css={theme => [{ marginBottom: theme.space.s48 }, hasDarkBackground && { color: theme.colors.white }]}
            data-testid="template-grid-heading"
            variant={headingVariant}
          >
            {heading}
          </Heading>
        )}
        {text && (
          <Text
            css={theme => [{ marginBottom: theme.space.s40 }, hasDarkBackground && { color: theme.colors.white }]}
            data-testid="template-grid-text"
            variant="paragraphL400"
          >
            {text}
          </Text>
        )}
        <Box
          css={theme => ({
            width: '100%',
            display: 'grid',
            gap: theme.space.s32,
            gridTemplateColumns: '1fr',
            [theme.breakpoints.bp500.min]: {
              gridTemplateColumns: 'repeat(2, 1fr)',
            },
            [theme.breakpoints.bp900.min]: {
              gridTemplateColumns: 'repeat(3, 1fr)',
            },
          })}
        >
          {cards.map(({ heading: cardHeading, image, template_id, link: cardLink }, i) => (
            <TemplateCard
              key={`${kebabCase(heading)}-${i}`}
              heading={cardHeading}
              link={{
                ...cardLink,
                final_url: cardLink?.final_url || `${registerUrl}/${template_id}`,
                accessibleDescription: withSmartling('mktg_consideration_template', {
                  template: cardHeading || '',
                }),
                beaconEvent,
              }}
              media={{
                image: {
                  ...(image ?? {}),
                  filename_disk:
                    image?.filename_disk || `https://cdn.lucid.app/documents/thumb/${template_id}/0/644/NULL/750`,
                } as Lucid.Directus.Generated.Directus_Files,
              }}
            />
          ))}
        </Box>
        {link?.final_url && (
          <Box css={theme => ({ width: '100%', textAlign: 'right', marginTop: theme.space.s48 })}>
            <Link
              {...link}
              data-testid="template-grid-link"
              href={link?.final_url}
              iconAnimation="navigational"
              variant={hasDarkBackground ? 'primaryInverse' : 'primaryAlternate'}
            >
              {link?.default_link_text}
            </Link>
          </Box>
        )}
      </Container>
    </Box>
  ) : null;
};

export { TemplateGrid };
export default TemplateGrid;
